import { useTheme } from "../../../contexts/ThemeContext";

interface ShowcaseHeroProps {
  totalListings: number;
  totalInterests: number;
  onPostFlip: () => void;
}

export default function ShowcaseHero({ totalListings, totalInterests, onPostFlip }: ShowcaseHeroProps) {
  const { isDark } = useTheme();

  const STATS = [
    { label: "Active Listings", value: totalListings.toString(), icon: "ri-price-tag-3-line" },
    { label: "Interests Today", value: `${totalInterests}+`, icon: "ri-heart-line" },
    { label: "Categories", value: "6", icon: "ri-layout-grid-line" },
    { label: "Avg. Margin", value: "78%", icon: "ri-line-chart-line" },
  ];

  return (
    <section className="pt-36 pb-16 px-8 lg:px-16">
      <div className="max-w-7xl mx-auto">

        {/* Disclaimer banner */}
        <div className={`flex items-start gap-3 px-5 py-4 rounded-xl border mb-10 ${isDark ? "bg-amber-500/5 border-amber-500/15 text-amber-300/70" : "bg-amber-50 border-amber-200 text-amber-700"}`}>
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="ri-information-line text-sm" />
          </div>
          <p className="font-inter text-sm leading-relaxed">
            <span className="font-semibold">This is a preview, not a live marketplace.</span>
            {" "}The listings below are fictional examples designed to show you what the FlipIQ community showcase will look like — including categories, interest alerts, and seller notifications. No real products are being sold here.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10 mb-14">
          <div className="max-w-2xl">
            <span className={`inline-flex items-center gap-2 font-inter text-xs font-medium px-3 py-1.5 rounded-full mb-6 ${isDark ? "bg-white/5 text-[#888]" : "bg-black/6 text-[#666]"}`}>
              <i className="ri-community-line text-xs" />
              Community Showcase
            </span>
            <h1 className={`font-syne text-5xl lg:text-6xl font-bold mb-5 leading-tight ${isDark ? "text-white" : "text-[#111111]"}`}>
              What the community<br />
              <span className={isDark ? "text-[#444]" : "text-[#cccccc]"}>is flipping right now.</span>
            </h1>
            <p className={`font-inter text-base leading-relaxed max-w-lg ${isDark ? "text-[#555555]" : "text-[#666666]"}`}>
              A glimpse of what&apos;s coming — example flips across cards, sneakers, tech, games, clothing, and computers. This is how the community will post, browse, and express interest when FlipIQ launches.
            </p>
          </div>

          <button
            type="button"
            onClick={onPostFlip}
            className={`flex items-center gap-2.5 font-inter text-sm font-medium px-6 py-3.5 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap flex-shrink-0 ${isDark ? "bg-white text-black hover:bg-[#e0e0e0]" : "bg-[#111] text-white hover:bg-[#333]"}`}
          >
            <i className="ri-add-line text-base" />
            Post Your Flip
          </button>
        </div>

        {/* Stats bar */}
        <div className={`grid grid-cols-2 lg:grid-cols-4 gap-4 p-6 rounded-2xl border ${isDark ? "bg-white/[0.02] border-white/6" : "bg-white border-black/8"}`}>
          {STATS.map((stat) => (
            <div key={stat.label} className="flex items-center gap-3">
              <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 ${isDark ? "bg-white/6" : "bg-black/5"}`}>
                <i className={`${stat.icon} text-sm ${isDark ? "text-[#aaa]" : "text-[#555]"}`} />
              </div>
              <div>
                <p className={`font-syne font-bold text-lg leading-none mb-0.5 ${isDark ? "text-white" : "text-[#111]"}`}>{stat.value}</p>
                <p className={`font-inter text-[10px] uppercase tracking-wider ${isDark ? "text-[#555]" : "text-[#aaa]"}`}>{stat.label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
